import { useCallback, useEffect, useRef, useState } from 'react';

export interface AccountList<T> {
  /** Null until known: before the account loads, or while the list does. */
  readonly items: readonly T[] | null;
  /** Loads the list again, as after something in it is made, changed, or deleted. */
  readonly reload: () => Promise<void>;
  /** Changes the list as it's known here, at once, such as before the server confirms it. */
  readonly patch: (change: (items: readonly T[]) => readonly T[]) => void;
}

const NONE: readonly never[] = [];

/**
 * Something the signed-in writer keeps in their account, such as their tones, for menus to
 * offer. `owner` is who's signed in: null for nobody, who has none, and undefined until that's
 * known. It's edited on the site, so it loads again whenever the window comes back to the front.
 */
export function useAccountList<T>(
  load: () => Promise<readonly T[]>,
  owner: string | null | undefined,
): AccountList<T> {
  const [loaded, setLoaded] = useState<{
    readonly owner: string;
    readonly items: readonly T[];
  } | null>(null);
  // Only the latest request's answer is kept, so an older one can't overwrite a newer.
  const latest = useRef(0);

  const reload = useCallback(async () => {
    if (!owner) return;
    const request = ++latest.current;
    const items = await load();
    if (request === latest.current) setLoaded({ owner, items });
  }, [load, owner]);

  const patch = useCallback((change: (items: readonly T[]) => readonly T[]) => {
    // A request in flight would answer with the list from before; this change is newer.
    latest.current += 1;
    setLoaded((current) => current && { ...current, items: change(current.items) });
  }, []);

  useEffect(() => {
    if (!owner) return;
    const refresh = () => {
      reload().catch(() => {
        // The menu keeps what it has; the next focus tries again.
      });
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
    };
  }, [owner, reload]);

  if (owner === undefined) return { items: null, reload, patch };
  if (owner === null) return { items: NONE, reload, patch };
  return { items: loaded?.owner === owner ? loaded.items : null, reload, patch };
}
